import { VehicleSearch } from 'app/pages/library/videos/videoList/components/SendAndShare';
import { FieldArray, useFormikContext } from 'formik';
import {
  IQuickVideo,
  IQuickVideoPartOption,
  IQuickVideoPartPositionOption,
  IQuickVideoTestSendForm,
  QuickVideoPartType,
} from 'lib/api/quickVideo/types';
import { FormikInputField } from 'lib/components/formik/FormikInputField';
import { Flex } from 'lib/components/styles/layout';
import React from 'react';
import { useTheme } from 'styled-components/macro';
import { Dictionary } from 'lodash';
import { Typography } from 'react-covideo-common';
import { TestSendQuickVideoPartComponent } from '../TestSendQuickVideoPartComponent';
import { CustomInfoMessage } from '../style';

type TestSendFormProps = {
  allVideoPartOptions: Dictionary<IQuickVideoPartOption[]>;
  positions: IQuickVideoPartPositionOption[];
  quickVideo: IQuickVideo;
};

export const TestSendForm = ({
  allVideoPartOptions,
  positions,
  quickVideo,
}: TestSendFormProps) => {
  const { setFieldValue, values, errors } =
    useFormikContext<IQuickVideoTestSendForm>();

  const theme = useTheme();
  const isVehicleRequired = values.quickVideoParts.some(
    qvp => qvp.partType === QuickVideoPartType.AI_VIN_REEL
  );

  return (
    <Flex width='899px' gap='20px'>
      <Flex gap='20px'>
        <Typography
          fontWeight={600}
          variant='normal'
          color={theme.colors.neutral[80]}
        >
          Recipient Details
        </Typography>

        <FormikInputField
          name='recipientName'
          label='Recipient Name'
          placeholder='Enter recipient name'
          isRequired={true}
          extendStyles={{
            labelStyles: {
              color: theme.colors.neutral[100],
            },
            errorWrapper: {
              height: 0,
            },
            placeholder: { color: theme.colors.neutral[80], opacity: 0.7 },
          }}
        />

        <Flex gap='5px' margin='0 0 5px 0'>
          <Typography
            variant='small'
            fontWeight={500}
            color={theme.colors.neutral[100]}
          >
            Vehicle
            {isVehicleRequired && (
              <span
                style={{ color: theme.colors.primary[100], marginLeft: '2px' }}
              >
                *
              </span>
            )}
          </Typography>
          <VehicleSearch
            fetchSold={false}
            onChangeVehicle={vehicle => {
              setFieldValue('inventoryItemId', vehicle?.itemId || 0);
            }}
            customSelectStyle={{
              menu: base => {
                return {
                  ...base,
                  backgroundColor: theme.colors.white[100],
                };
              },
              placeholder: base => {
                return {
                  ...base,
                  margin: '0 0 0 8px',
                };
              },
            }}
          />

          {errors?.inventoryItemId && (
            <CustomInfoMessage>{errors.inventoryItemId}</CustomInfoMessage>
          )}
        </Flex>

        <FormikInputField
          name='recipientEmail'
          label='Recipient Email'
          isRequired={false}
          disabled
          extendStyles={{
            labelStyles: {
              color: theme.colors.neutral[100],
              opacity: 1,
            },
            errorWrapper: {
              height: 0,
            },
          }}
        />
      </Flex>
      <Flex gap='15px'>
        <Typography
          variant='normal'
          fontWeight={600}
          color={theme.colors.neutral[80]}
        >
          Video Details
        </Typography>

        {quickVideo.description && (
          <Typography
            variant='small'
            fontWeight={400}
            color={theme.colors.neutral[60]}
          >
            {quickVideo.description}
          </Typography>
        )}

        <Flex flexWrap='nowrap' flexDirection='row' gap='15px'>
          <FieldArray name='quickVideoParts'>
            {({ replace }) =>
              values.quickVideoParts.map((part, index) => {
                const allPositionOptions =
                  allVideoPartOptions[part.partPosition];
                const partOption = allPositionOptions.find(
                  partOpt => partOpt.value === part.partType
                );
                const positionLabel = positions.find(
                  p => p.value === part.partPosition
                );

                return (
                  <TestSendQuickVideoPartComponent
                    quickVideoPart={partOption}
                    position={positionLabel}
                    index={index}
                    replace={replace}
                    key={`test-send-quick-video-part-${part.quickVideoPartId}`}
                  />
                );
              })
            }
          </FieldArray>
        </Flex>
      </Flex>
    </Flex>
  );
};
